/* globals document */

import {compact, find, last, isString} from 'lodash';
import {
  titleize,
  humanize,
  strRightBack,
} from '@packages/utilities/string-utils';
import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {TAG_LABEL_MAP} from '@packages/shared/LegacyStyleSystem/constants/DefaultTagStyles';

import type {
  SelectorToken,
  SelectorChunk,
} from '@packages/systems/style-legacy/rules';

import {
  PSEUDO_PREFIX,
  STATE_PREFIX,
  STATES_ORDER,
  PSEUDOS,
  COMBINATORS,
  REDIRECTED_PSEUDOS,
} from '@packages/systems/core/constants/Selectors';
// eslint-disable-next-line webflow/module-top-level-imports
import {
  CombinatorKey,
  StyleBlockId,
} from '@packages/systems/style/types/legacy';
import {
  getPseudoStateKey,
  normalizePseudoState,
} from '@packages/systems/core/utils/StyleTypes';
import {Qualified, getPluginName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Component} from '@packages/systems/wfdl';
import {reportBugOnce} from '@packages/utilities/reporter';
import {cacheMemo} from '@packages/utilities/memo';
import {VariantDataMap} from './VariantData';
import {parseVariantStateKey, VariantStateKey} from './VariantStateKey';
import {TARGET_AI_SITE_GEN_LIBRARY_SLUG} from '@packages/systems/core-constants';

export * from './VariantStateKey';
export * from './VariantData';

export * from './VariantStateKey';
export * from './VariantData';

export const isClassToken = ({className, isElementStyle}: SelectorToken) =>
  Boolean(className) && !isElementStyle;

/**
 * NOTE: This function is inlined here instead of being imported from the Optimization package so we don't bloat this package with
 * the Optimization package's dependencies.
 *
 * There is a test here to catch value drift: https://github.com/webflow/webflow/blob/177a922d7fea05156cc9140678ce26f881f47b9d/packages/systems/style-legacy/selectors/index.test.ts#L331-L339
 */
const INTELLIMIZE_SELECTOR_ATTRIBUTE = 'data-intellimize-var-';

/**
 * Whether the CSS engine can parse `selector`. `CSSStyleSheet.insertRule`
 * throws on a selector it can't parse, which takes down canvas style sync, so
 * ask the engine before emitting one. Outside the browser there is no
 * stylesheet to break and no engine to ask, so assume it parses and keep
 * emitting what we always have.
 *
 * Only selectors outside `pseudoMap` reach the engine, so a handful of
 * distinct keys covers even a site carrying corrupted style-block data.
 */
const isSelectorParseable = cacheMemo(20)((selector: string): boolean => {
  const css = globalThis.CSS;
  if (typeof css?.supports !== 'function') {
    return true;
  }

  try {
    return css.supports(`selector(${selector})`);
  } catch {
    return false;
  }
});

const isCurrentVariationDataAttribute = (attribute: string | undefined) => {
  return attribute && attribute.startsWith(INTELLIMIZE_SELECTOR_ATTRIBUTE);
};

const isDataAttributeSelector = (pseudoState: string) =>
  /^data-/.test(pseudoState);

/**
 * Builds a simple selector token object, only setting props as needed.
 * @param  {String} options.tagName      (Optional) If the token is a tag, the CSS name of the tag, e.g. 'h1';
 * @param  {String} options.className    (Optional) If the token is a user-entered class name, the full StyleBlock name, e.g. "Hero Section"
 * @param  {String} options.customNodeState (Optional) If the token is a special Webflow derived state, the CSS state tag e.g. "current", "open"
 * @param  {String} options.pseudoState  (Optional) If the token is a pseudo state, the CSS pseudo name, e.g. "visited", "focus", etc
 * @return {Object}                      A selector token object.
 */
export function buildSelectorToken({
  tagName,
  className,
  customNodeState,
  pseudoState,
  variantState,
  namespace,
  origin,
  isElementStyle,
}: SelectorToken = {}) {
  const token: Record<string, any> = {};

  if (tagName && tagName.trim()) {
    token.tagName = tagName.trim();
  }

  if (className && className.trim()) {
    token.className = className.trim();
  }

  if (customNodeState && customNodeState.trim()) {
    token.customNodeState = customNodeState.trim();
  }

  if (pseudoState && pseudoState.trim()) {
    token.pseudoState = pseudoState.trim();
  }

  if (variantState && variantState.trim()) {
    token.variantState = variantState.trim();
  }

  if (namespace) {
    token.namespace = namespace;
  }

  if (origin) {
    token.origin = origin;
  }

  if (isElementStyle) {
    token.isElementStyle = isElementStyle;
  }

  return token as SelectorToken;
}

/**
 * Generates the full CSS name for a selector.
 * @param  {Object[]} chunks The list of chunks from StyleBlockUtils.decorateStyleBlock()
 * @return {String}          A proper CSS selector string, e.g. ".body-text p:hover"
 */
export function getSelectorText(
  chunks: Array<SelectorChunk>,
  slugMap: Plugins.PluginsType['slugMap'],
  variantDataMap: VariantDataMap
) {
  const selectorPieces: Array<any | string | CombinatorKey> = [];

  /**
   * Variants get to choose how to generate their selector text via the
   * `VariantData` interface, so we track all active variants, then apply them
   * at the end of this function.
   */
  const activeVariants: VariantStateKey[] = [];

  for (const chunk of chunks) {
    let tokens = chunk.tokens;
    let combinator = chunk.combinator;

    // If any token is an Intellimize variation data attribute, extract it and
    // prepend it as a descendant selector, e.g. `[data-intellimize-var-123] .heading`.
    // We search all tokens (not just the last) because a variantState token for
    // component style variants may follow the optimization token.
    const optimizationToken = tokens.find((token) =>
      isCurrentVariationDataAttribute(token.pseudoState)
    );

    if (optimizationToken) {
      tokens = tokens.filter((token) => token !== optimizationToken);
      selectorPieces.push(getTokenCss(optimizationToken, slugMap));
      combinator = COMBINATORS.DESCENDANT;
    }

    for (const [index, token] of tokens.entries()) {
      if (token.variantState != null) {
        activeVariants.push(token.variantState);
        continue;
      }

      // If this is the first token in a chunk, need to render out the combinator
      if (index === 0) {
        let combinatorCss = '';

        switch (combinator) {
          case COMBINATORS.DESCENDANT:
            combinatorCss = ' '; // .button .cart
            break;
          case COMBINATORS.COMBO:
            combinatorCss = ''; // .button.cart
            break;
          default:
            combinatorCss = combinator || ''; // .button > .cart, .button + .cart
            break;
        }

        selectorPieces.push(combinatorCss);
      }

      const tokenCss = getTokenCss(token, slugMap);
      // A token we can't render means the whole rule is unsafe to emit;
      // returning null drops it, the same way an unknown variant does below.
      if (tokenCss == null) {
        return null;
      }
      selectorPieces.push(tokenCss);
    }
  }

  let selector = selectorPieces.join('');

  // Finally, we apply all our variant selector modifications:
  for (const variantKey of activeVariants) {
    const variantData = variantDataMap.get(variantKey);
    if (variantData == null) {
      // If no matching variant data was found in VariantDataMap, we don't want to
      // add a CSS rule so we'll return null & filter it out.
      return null;
    }
    selector = variantData.getSelector(selector);
  }
  return selector;
}

/**
 * Computes the specificity score for the entire selector.
 * @param  {Object[]} chunks The list of chunks from StyleBlockUtils.decorateStyleBlock()
 * @return {Number}          The CSS specificity score, e.g. ".hero-section:hover" -> 10 + 1 = 11
 */
export function getSelectorScore(chunks: Array<SelectorChunk>) {
  let score = 0;

  chunks.forEach((chunk) => {
    chunk.tokens.forEach((token) => {
      const tokenScore = getTokenScore(token);
      if (isNaN(tokenScore)) {
        console.error('Unrecognized token score: %o', token);
      } else {
        score += tokenScore;
      }
    });
  });

  return score;
}

/**
 * Gets a human-readable label for internal token information.
 * @param  {String} options.tagName      (Optional) If the token is a tag, the CSS name of the tag, e.g. 'h1';
 * @param  {String} options.className    (Optional) If the token is a user-entered class name, the full StyleBlock name, e.g. "Hero Section"
 * @param  {String} options.customNodeState (Optional) If the token is a special Webflow derived state, the CSS state tag e.g. "current", "open"
 * @param  {String} options.pseudoState  (Optional) If the token is a pseudo state, the CSS pseudo name, e.g. "visited", "focus", etc
 * @return {String}                      A label used to represent this token in UI and debug views.
 */
export function getTokenLabel(
  {
    tagName,
    className,
    customNodeState,
    pseudoState,
    variantState,
    isElementStyle,
  }: SelectorToken,
  currentComponent?: Component.ComponentType
) {
  if (variantState != null && currentComponent != null) {
    // Get variants for the current component
    const variantFields = Component.getVariants(currentComponent);
    if (variantFields != null) {
      const [fieldId, optionId] = parseVariantStateKey(variantState);
      if (fieldId != null && optionId != null) {
        const variantOptions = variantFields[fieldId];
        if (variantOptions) {
          // Find the option that matches the variant state and return the display name
          const option = variantOptions.find(({id}) => id === optionId);
          if (option) {
            return option.displayName;
          }
        }
      }
    }
  }

  if (isElementStyle) {
    return 'Element';
  }

  if (tagName && tagName.trim() !== '') {
    const trimmed = tagName.trim();
    const label = Object.prototype.hasOwnProperty.call(TAG_LABEL_MAP, trimmed)
      ? TAG_LABEL_MAP[trimmed]
      : undefined;
    return label ?? 'All ' + titleize(trimmed) + 's';
  }

  if (className && className.trim() !== '') {
    return className.trim();
  }

  if (customNodeState && customNodeState.trim() !== '') {
    if (customNodeState === REDIRECTED_PSEUDOS.FOCUS) {
      return 'Focused';
    }
    if (customNodeState === REDIRECTED_PSEUDOS.FOCUS_VISIBLE) {
      return 'Focused (keyboard)';
    }

    if (customNodeState === REDIRECTED_PSEUDOS.CHECKED) {
      return 'Checked';
    }

    if (customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_PILL_SELECTED) {
      return 'Selected';
    }

    if (
      customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_PILL_DISABLED ||
      customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_ADD_TO_CART_DISABLED ||
      customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_BUY_NOW_DISABLED
    ) {
      return 'Disabled';
    }

    return titleize(customNodeState);
  }

  return getPseudoStateLabel(pseudoState);
}

export function getPseudoStateLabel(pseudoState: string | undefined) {
  if (pseudoState && pseudoState.trim() !== '') {
    if (pseudoState === 'focus') {
      return 'Focused';
    } else if (pseudoState === 'focus-visible') {
      return 'Focused (keyboard)';
    } else if (pseudoState === 'focus-within') {
      return 'Focused within';
    } else if (pseudoState === 'active') {
      return 'Active';
    } else if (pseudoState === 'placeholder') {
      return 'Placeholder';
    } else if (pseudoState === 'popover-open') {
      return 'Popover open';
    } else if (pseudoState === 'backdrop') {
      return 'Backdrop';
    } else if (pseudoState === 'first-child') {
      return 'First Item';
    } else if (pseudoState === 'last-child') {
      return 'Last Item';
    } else if (pseudoState === 'nth-child(odd)') {
      return 'Odd Items';
    } else if (pseudoState === 'nth-child(even)') {
      return 'Even Items';
    } else if (normalizePseudoState(pseudoState) === 'lang') {
      return 'Current Locale';
    } else if (isCurrentVariationDataAttribute(pseudoState)) {
      return 'Current Variation';
    } else {
      return titleize(humanize(pseudoState));
    }
  }

  return 'Error!';
}

/**
 * Produce a class name prefix from a library slug, given a StyleBlock's
 * `origin` field and a `slugMap` of pluginID->library slug.
 */
export function getUserlandPrefix(
  origin: Qualified<StyleBlockId>,
  slugMap: Plugins.PluginsType['slugMap']
) {
  let userlandPrefix = null;
  if (origin) {
    const pluginId = getPluginName(origin);
    const slug = slugMap[pluginId];
    if (!slug) {
      // FIXME: Right now there are cases where this ends up getting called
      // after style blocks load, but before all plugins load, and thus we don't
      // have a `slugMap` to reference to render our styles.
      //
      // Therefore this isn't so much an error as an expected temporary broken
      // state.
      //
      // We probably need a way to track whether our plugins have been loaded
      // and wait for that before we attempt rendering styles.
      reportBugOnce(
        '[Libraries][getUserlandPrefix]',
        `Could not determine prefix slug for CSS token with origin ${pluginId}`,
        {
          data: {origin},
          team: 'structure',
          ticket: 'https://webflow.atlassian.net/browse/SCALE-625',
        }
      );
      // style blocks created by AI Site Builder have an origin, as they were created from a workspace library,
      // but they should not be rendered in CSS with a prefix.
    } else if (!slug.startsWith(TARGET_AI_SITE_GEN_LIBRARY_SLUG)) {
      userlandPrefix = slug;
    }
  }
  return userlandPrefix;
}

export function getTokenCss(
  {
    tagName,
    className,
    namespace,
    origin,
    customNodeState,
    pseudoState,
    isElementStyle,
  }: SelectorToken,
  slugMap: Plugins.PluginsType['slugMap']
) {
  if (tagName && tagName.trim() !== '') {
    return tagName.trim().toLowerCase();
  }

  if (isElementStyle && className) {
    return '.' + className.trim().toLowerCase();
  }

  if (className && className.trim() !== '') {
    const userlandPrefix = origin ? getUserlandPrefix(origin, slugMap) : null;
    return '.' + (toClassName(className, {userlandPrefix, namespace}) || '');
  }

  if (customNodeState && customNodeState.trim() !== '') {
    return '.' + STATE_PREFIX + customNodeState.trim().toLowerCase();
  }

  if (pseudoState && pseudoState.trim() !== '') {
    if (pseudoState && pseudoState.trim() !== '') {
      const rawPseudoState = pseudoState.trim().toLowerCase();

      if (isDataAttributeSelector(rawPseudoState)) {
        return `[${rawPseudoState}]`;
      }

      // `:pressed` is not a real CSS pseudo-class — a historical bug let it
      // get persisted into style-block variant data. Emitting `:pressed`
      // makes CSSStyleSheet.insertRule throw a SyntaxError and crashes the
      // Designer canvas sync. Render already-persisted data as `:active`
      // so the canvas can keep syncing until the legacy data is migrated
      // out.
      const newPseudoState =
        rawPseudoState === 'pressed' ? 'active' : rawPseudoState;

      const prefix =
        newPseudoState === 'placeholder' || newPseudoState === 'backdrop'
          ? '::'
          : ':';
      const selector = prefix + newPseudoState;

      // A selector the CSS engine can't parse makes
      // `CSSStyleSheet.insertRule` throw and takes down canvas style sync.
      // Corrupted style-block variant data has reached this point before (see
      // the `pressed` shim above), so drop the token rather than emit a
      // selector we know cannot be inserted. A pseudo state the Style Panel
      // offers is emitted either way: a browser too old to parse it fails the
      // same way it always has, rather than silently losing the style.
      if (
        getPseudoStateKey(newPseudoState) == null &&
        !isSelectorParseable(selector)
      ) {
        // eslint-disable-next-line webflow/better-bug-reporting -- ticket lives in Linear, not Jira.
        reportBugOnce(
          '[Styles][getTokenCss]',
          `Dropped style token with an invalid pseudo state: ${rawPseudoState}`,
          {
            data: {pseudoState: rawPseudoState},
            team: 'styles',
            ticket: 'https://linear.app/webflow/issue/DES-7649',
          },
          {severity: 'warning'}
        );
        return null;
      }

      return selector;
    }
  }

  return '-error-';
}

export function getTokenScore({
  tagName,
  className,
  customNodeState,
  pseudoState,
  variantState,
}: SelectorToken) {
  if (tagName && tagName.trim()) {
    return 1;
  }

  if (className && className.trim()) {
    return 10;
  }

  if (customNodeState && customNodeState.trim()) {
    return 10;
  }

  if (pseudoState && pseudoState.trim()) {
    return 1;
  }

  if (variantState && variantState.trim()) {
    // Note: For now we hard-code the score impact of an applicable custom
    // variant to match that of a class, even though its selector is unknown in
    // this context.
    //
    // In the future we may need to modify this to be derived from the
    // `VariantData` implementation.
    //
    // Tracking here: https://webflow.atlassian.net/browse/STYL-797
    return 10;
  }

  // Return NaN instead of 0 since there could be cases in the future where 0 is a proper token score
  return NaN;
}

const testSelectorSupport = (selector: string) => {
  try {
    document.querySelector(selector);
    return true;
  } catch (e: any) {
    return false;
  }
};

const SUPPORTS_WHERE = testSelectorSupport(':where()');
const SUPPORTS_FOCUS_VISIBLE = testSelectorSupport(':focus-visible');

/**
 * Cleans up selector text to be ready for entry into a style sheet.
 * @param  {String}   selectorText        The original selector text, e.g. from StyleBlocks.
 * @return {String}                       The cleaned up selector text.
 */
export function cssifyText(selectorText: string) {
  selectorText = collapseSpaces((selectorText || '').toLowerCase()).trim();

  // If the selector is not defined in any way
  if (!selectorText) {
    return null;
  }

  // If the selector text contains a pseudo portion, create a fake-pseudo equivalent so we can force
  // style display even when the pseudo has not been triggered natively
  // e.g. ".button:hover {}" becomes ".button:hover, .button.-wfp-hover {}"
  // Backdrop pseudo-elements cannot use the fake-pseudo preview class.

  if (
    selectorText.indexOf('.' + PSEUDO_PREFIX) < 0 &&
    !selectorText.includes('::placeholder') &&
    !selectorText.includes('::backdrop')
  ) {
    const pseudoText = selectorText.replace(
      /:(hover|active|focus|visited|disabled)/gi,
      '.' + PSEUDO_PREFIX + '$1'
    );

    const hasPseudos = selectorText !== pseudoText;
    const excludePreviewModeSelector = SUPPORTS_WHERE
      ? ':where(html:not(.wf-design-mode)) ' // adds 0 specificity
      : 'html:not(.wf-design-mode) '; // adds specificity which results in this bug: https://app.clickup.com/t/2310520/BUG-2742

    if (hasPseudos) {
      // replace the real pseudo-selector with the fake class version to prevent
      // :visited, :focus, etc from triggering in the Designer Canvas when the
      // corresponding pseudo state is _not_ engaged. However, we want to apply
      // the real pseudo-selector in Designer Canvas when in Preview mode
      // NOTE: :hover is left in the Designer for legacy reasons
      selectorText = `${pseudoText}, ${
        /:hover/i.test(selectorText) ? '' : excludePreviewModeSelector
      }${
        !SUPPORTS_FOCUS_VISIBLE && /:focus-visible/i.test(selectorText)
          ? // if there is no native support for :focus-visible, use the polyfill selector instead
            selectorText.replace(
              /\b:focus-visible\b/gi,
              '[data-wf-focus-visible="true"]'
            )
          : selectorText
      }`;
    }
  }

  return selectorText;
}

/**
 * Pulls out a state enum from a collection of class names.
 * @param  {String[]}  cssClasses   A list of class names, collected from a DOM node.
 * @return {STATES}
 */
export function detectStateFromClasses(cssClasses: Array<string> = []) {
  const stateClassName = find(compact(cssClasses), function (className) {
    return className.indexOf(STATE_PREFIX) === 0;
  });

  const stateValue = stateClassName
    ? strRightBack(stateClassName, STATE_PREFIX)
    : null;
  // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type '"current" | "open" | "redirected-checked" | "redirected-focus" | "redirected-focus-visible" | "ecommerce-pill-selected" | "ecommerce-pill-disabled" | "ecommerce-add-to-cart-disabled" | "ecommerce-buy-now-disabled"'.
  return STATES_ORDER[STATES_ORDER.indexOf(stateValue)] || null;
}

/**
 * Gets pseudo states that are pre-requisites for a specific state.
 * @param  {String}   pseudo The name of the current pseudo, e.g. "active"
 * @return {String[]}
 */
export function getUpstreamPseudos(pseudo: string) {
  if (pseudo === PSEUDOS.ACTIVE) {
    return [PSEUDOS.HOVER];
  }
  if (pseudo === PSEUDOS.FOCUS_VISIBLE) {
    return [PSEUDOS.FOCUS, PSEUDOS.FOCUS_WITHIN];
  }
  if (pseudo === PSEUDOS.FOCUS) {
    return [PSEUDOS.FOCUS_WITHIN];
  }

  return [];
}

/**
 * Returns whether the given selector chunks constitute a 'tag' or 'class' selector.
 * @param  {Object[]} chunks The list of chunks in a selector.
 * @return {String}
 */
export function getType(chunks: null | Array<SelectorChunk>) {
  if (!chunks) {
    return null;
  }
  const lastChunk = last(chunks);
  // @ts-expect-error - TS18048 - 'lastChunk' is possibly 'undefined'.
  const tagClassTokens = lastChunk.tokens.filter((token) => {
    const {tagName, className} = token;
    return Boolean(tagName) || Boolean(className);
  });
  if (tagClassTokens.length > 0) {
    const lastTagClassToken = last(tagClassTokens);
    // @ts-expect-error - TS18048 - 'lastTagClassToken' is possibly 'undefined'.
    if (lastTagClassToken.tagName) {
      return 'tag';
      // @ts-expect-error - TS18048 - 'lastTagClassToken' is possibly 'undefined'.
    } else if (lastTagClassToken.className) {
      return 'class';
    } else {
      return null;
    }
  } else {
    return null;
  }
}

/**
 * Returns whether the given chunks constitute a nested (descendant) selector.
 * @param  {Object[]} chunks The list of chunks in a selector.
 * @return {bool}
 */
export function isNested(chunks: null | Array<SelectorChunk>) {
  return chunks && chunks.length > 1;
}

/**
 * If given chunks constitute tag selector, returns the tagName
 * @param {Object[]} chunks
 * @return {String}
 */
export function getTagName(chunks: null | Array<SelectorChunk>) {
  if (!chunks) {
    return null;
  }
  const lastChunk = last(chunks);
  // @ts-expect-error - TS18048 - 'lastChunk' is possibly 'undefined'.
  const tagToken = lastChunk.tokens.filter((token) => {
    return Boolean(token.tagName);
  });
  if (tagToken.length > 0) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    return last(tagToken).tagName;
  } else {
    return null;
  }
}

/**
 * Converts a string to something that is usable as a CSS class and checks for conflicts with Webflow classes.
 * Returns an empty string, '', if input is invalid.
 * Use `isValidClassName` to check if input could be made into valid classname string.
 */
const WEBFLOW_PREFIX = 'w-';

const DASH = '-';
const SPACE = ' ';
const UNDERSCORE = '_';

const CODE_LOWERCASE_W = 119;
const CODE_LOWERCASE_F = 102;
const CODE_DASH = 45;

/**
 * # FOR INTERNAL STYLE ENGINE USE ONLY
 *
 * Produces a class name given a set of options, which are meant to be derived
 * from StyleBlock and environment metadata.
 */
export function toClassName(
  name: string,
  {
    userlandPrefix,
    namespace = null,
    propKey = null,
    propValue = null,
    allowReservedPrefix = false,
  }: {
    /** A prefix to apply to the class. NOT validated for class-name safety. */
    userlandPrefix: string | null;
    /** Used by built-in Webflow plugins to prefix default styles. See also `StyleBlock::namespace` */
    namespace?: string | null;
    /** Used for "conditional styles"; the unique key used to designate the condition, if any*/
    propKey?: string | null;
    /** Used for "conditional styles"; the value corresponding to they provided `propKey`, if any */
    propValue?: string | null;
    /** When true, skip the `_` prefix mangling for `w-` and `wf-` prefixed classes.
     *  Used for bootflow combo selectors where the class must match the render-injected name exactly. */
    allowReservedPrefix?: boolean;
  }
): string {
  const conditionalSuffix =
    propKey && propValue ? `--${propKey.trim()}-${propValue.trim()}` : '';

  if (namespace) {
    // If it's a namespaced class, we don't have to check for conflicts with Bootflow.
    // Use case: For when classNames are autogenerated.
    const fullName = WEBFLOW_PREFIX + namespace + ' ' + name;

    // Note: This scenario should not happen as we reserve `namespace` for
    // Webflow-owned styles, while `userlandPrefix` is only for user-specified
    // styles.
    if (userlandPrefix != null) {
      throw new Error(
        `Did not expect userlandPrefix to be provided along with namespace`
      );
    }

    return createClassName(fullName, null) + conditionalSuffix;
  } else {
    // If a user happens to name their class in a way that will render out to "wf-...", need to prefix
    // since the publishing/syncing code ignores all "wf-" classes (they are reserved in the designer).
    //
    // Don't allow conflicts with Bootflow 'w-' and 'w--' prefixed classes.
    // Use case: For custom classes created by the user in the Style Tab.
    const classname = createClassName(name, userlandPrefix);

    if (!allowReservedPrefix) {
      const ch0 = classname.charCodeAt(0);
      if (ch0 === CODE_LOWERCASE_W /* 'w' */) {
        const ch1 = classname.charCodeAt(1);
        if (ch1 === CODE_DASH /* '-' */) {
          return '_' + classname + conditionalSuffix;
        } else if (ch1 === CODE_LOWERCASE_F /* 'f' */) {
          const ch2 = classname.charCodeAt(2);
          if (ch2 === CODE_DASH /* '-' */) {
            return '_' + classname + conditionalSuffix;
          }
        }
      } else if (ch0 > 47 && ch0 < 58 /* [0-9] */) {
        // If the class name starts with a number, then we need to prepend an underscore to make it valid
        // A dash (-) should be valid too, but Chrome barfs when trying to create a native selector with it.
        return '_' + classname + conditionalSuffix;
      }
    }

    // Otherwise, return the classname.
    return classname + conditionalSuffix;
  }
}

// Check if a charCode falls in the numeric range.
// Equivalent regex: [0-9]
function isNumericCode(code: number): boolean {
  return code > 47 && code < 58;
}

function isUpperAlphaCode(code: number): boolean {
  // upper ascii alpha code range
  return code > 64 && code < 91;
}

function isLowerAlphaCode(code: number): boolean {
  // lower ascii alpha code range
  return code > 96 && code < 123;
}

/**
 * Check if a charCode falls in the lowercase or uppercase ascii letter range.
 * Equivalent regex: [A-Za-z]
 */
function isAlphaCode(code: number): boolean {
  return isUpperAlphaCode(code) || isLowerAlphaCode(code);
}

function isAlphaNumericCode(code: number): boolean {
  return isNumericCode(code) || isAlphaCode(code);
}

/**
 * Check if a given string character is ascii alphanumeric.
 */
function isAlphaNumericChar(ch: string) {
  const code = ch.charCodeAt(0);
  return isAlphaNumericCode(code);
}

function isInvalidOuterCharacter(ch: string): boolean {
  return ch === SPACE || ch === UNDERSCORE || ch === DASH;
}

function isInvalidInteriorCharacter(ch: string): boolean {
  return (
    ch === SPACE ||
    (ch !== DASH && ch !== UNDERSCORE && !isAlphaNumericChar(ch))
  );
}

/**
 * Convert an input string into a string that could become a valid css selector string.
 *
 * # Description
 *
 * In the past we relied on multiple regex replace calls to handle this logic. The original looked like this:
 *
 * ```js
 * return string
 *  .toLowerCase()
 *  .replace(/[^0-9a-z_-]/g, ' ')
 *  .replace(/^[ _-]+|[ _-]+$/g, '')
 *  .replace(/\s+/g, '-');
 * ```
 *
 * Here's a rundown of what we are doing here:
 * - if input starts with anything but a number, letter, underscore, or dash, trim it off.
 *   ex: "$%hello" => "hello"
 * - if input starts or ends with 1 or more spaces, dashes, or underscores in a row, trim it off.
 *   ex: "  hello" => "hello"
 *   ex: "--hello--" => "hello"
 *   ex: "__hello__" => "hello"
 *   ex: "_-hello__" => "hello"
 *   ex: "-hello-" => "-hello-"
 * - collapse interior whitespace/invalid characters into a single dash character, '-'.
 *   ex: "hello     world" => "hello-world"
 *   ex: "hello$@#world" => "hello-world"
 *
 * But at the moment v8's RegExpReplaceRT runtime call is really slow...
 * so we roll our own basic string parser to tackle this problem.
 *
 * Since we want to remove invalid leading/trailing characters we can start by
 * check the input string for where the invalid leading/trailing characters stop.
 * Once we have found the `leadingIndex` and `trailingIndex` we can start looking
 * at the interior of the string. We want to remove non-alphanumeric, dash, or
 * underscore characters from the input string and replace them with a dash separator.
 * We also want to collapse multiple occurences of "separator characters" (" ", "_", "-")
 * together into a single dash separator character, "-".
 */
export function createClassName(
  input: string,
  userlandPrefix: string | null
): string {
  const len = input.length;
  if (len === 0) return input;

  let buf = '';

  // Find end of leading invalid characters.
  let leading_index = 0;
  while (leading_index < len) {
    const ch = input[leading_index];
    // Search until we find a valid leading character.
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'. | TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (!isInvalidOuterCharacter(ch) && isAlphaNumericChar(ch)) {
      break;
    }
    leading_index++;
  }

  // Find start of trailing invalid characters.
  let trailing_index = input.length - 1;
  while (trailing_index >= 0) {
    const ch = input[trailing_index];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (!isInvalidOuterCharacter(ch)) {
      break;
    }
    trailing_index--;
  }

  // Now we know we are in the interior of the string.
  let i = leading_index;
  while (i <= trailing_index) {
    let ch = input[i];

    // Check if the current character is invalid.
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (isInvalidInteriorCharacter(ch)) {
      // If it is, skip that character.
      // eslint-disable-next-line no-labels
      check_for_invalid_characters: do {
        i++;

        // If we are still in the "interior", load the next character to check.
        if (i <= trailing_index) {
          ch = input[i];
        } else {
          // eslint-disable-next-line no-labels
          break check_for_invalid_characters;
        }
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      } while (isInvalidInteriorCharacter(ch));

      // Check if we skipped invalid characters and didn't hit the end.
      // We check this by seeing if when checking for invalid characters
      // we advanced `i` past our trailing index. This would mean that
      // we checked the last "interior" character and it was invalid.
      // If we still have characters left to check, add a DASH and the
      // character we found to not be invalid. The next iteration of the
      // loop will pick up where we preloaded i to.
      if (i <= trailing_index) {
        buf += DASH;
        // @ts-expect-error - TS18048 - 'ch' is possibly 'undefined'.
        buf += ch.toLowerCase();
        i++;
      }
    } else {
      // @ts-expect-error - TS18048 - 'ch' is possibly 'undefined'.
      buf += ch.toLowerCase();
      i++;
    }
  }

  if (userlandPrefix) {
    // Notes:
    //
    // - We ignore prefixes that are empty strings
    // - We use `--` as a separator, as this should not be possible to generate
    //   with user input alone. This ensures that we can use `userlandPrefix` to
    //   "namespace"/"scope" classes.
    buf = `${userlandPrefix}--${buf}`;
  }

  return buf;
}

/**
 * Determines if a user-entered string can be converted to a valid CSS class name.
 * @param  {String}  str The user-entered string.
 * @return {Boolean}     True if the string can be converted to a legitimate CSS class name.
 */
export function isValidClassName(str?: string | null) {
  const lastChar = str ? str[str.length - 1] : null;
  if (
    !str ||
    !isString(str) ||
    // Check to make sure the user-entered string doesn't end in a CSS selector terminal.
    // This can happen if a corrupted block name exists.
    lastChar === '.' ||
    lastChar === '#' ||
    lastChar === ':'
  ) {
    return false;
  }
  const classname = toClassName(str, {userlandPrefix: null});
  // The toClassName fn ensures we don't have leading/trailing whitespace.
  return classname.length > 0;
}
